import { useCallback, useEffect, useRef, useState } from 'react';

export type MicStatus = 'idle' | 'requesting' | 'active' | 'paused' | 'denied' | 'error';

/**
 * Records audio from a provided MediaStream, or requests one if not provided.
 */
export function useAudioRecording() {
  const [micStatus, setMicStatus] = useState<MicStatus>('idle');
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const ownStreamRef = useRef<MediaStream | null>(null);

  const startRecording = useCallback(
    async (existingStream?: MediaStream | null, deviceId?: string): Promise<MicStatus> => {
      setMicStatus('requesting');
      try {
        let stream: MediaStream;
        if (existingStream && existingStream.active) {
          stream = existingStream;
        } else {
          const constraints: MediaTrackConstraints = deviceId
            ? { deviceId: { exact: deviceId } }
            : {};
          stream = await navigator.mediaDevices.getUserMedia({ audio: constraints });
          ownStreamRef.current = stream;
        }

        const mimeType = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'].find((type) =>
          MediaRecorder.isTypeSupported(type),
        );

        const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
        mediaRecorderRef.current = recorder;
        chunksRef.current = [];

        recorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            chunksRef.current.push(event.data);
          }
        };

        recorder.start(1000);
        setMicStatus('active');
        return 'active';
      } catch (error) {
        if (error instanceof DOMException && error.name === 'NotAllowedError') {
          setMicStatus('denied');
          return 'denied';
        } else {
          setMicStatus('error');
          return 'error';
        }
      }
    },
    [],
  );

  const pauseRecording = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state === 'recording') {
      recorder.pause();
      setMicStatus('paused');
    }
  }, []);

  const resumeRecording = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state === 'paused') {
      recorder.resume();
      setMicStatus('active');
    }
  }, []);

  const stopRecording = useCallback((): Promise<Blob | null> => {
    return new Promise((resolve) => {
      const recorder = mediaRecorderRef.current;
      if (!recorder || recorder.state === 'inactive') {
        setMicStatus('idle');
        resolve(null);
        return;
      }

      recorder.onstop = () => {
        const blob =
          chunksRef.current.length > 0
            ? new Blob(chunksRef.current, { type: recorder.mimeType || 'audio/webm' })
            : null;
        chunksRef.current = [];
        mediaRecorderRef.current = null;
        setMicStatus('idle');
        resolve(blob);
      };

      recorder.stop();
      // Only stop tracks we own (not the shared monitoring stream)
      ownStreamRef.current?.getTracks().forEach((track) => track.stop());
      ownStreamRef.current = null;
    });
  }, []);

  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
      ownStreamRef.current?.getTracks().forEach((track) => track.stop());
    };
  }, []);

  return { micStatus, startRecording, stopRecording, pauseRecording, resumeRecording };
}
